/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable sort-keys */
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnInit,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { DepartmentInput } from 'generated/graphql-types';
import { Subscription } from 'rxjs';
import { extensionsRoute } from 'src/app/app-routing.module';
import { TwoColumnLayoutService } from 'src/app/common-components/column-layouts/two-column-list-layout/services/selected-id.service';
import { DetailView } from 'src/app/common-components/detail/detail-view';
import { DepartmentPrivileges } from '../../department-register/models/department-privileges';
import { DepartmentDetailSaveEvent } from '../models/department-detail-form-submit-event';
import { DepartmentDetailView } from '../models/department-detail-view.model';
import { DepartmentDetailFormService } from '../services/department-detail-form.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-department-detail-view',
    styleUrls: ['./department-detail-view.component.scss'],
    templateUrl: './department-detail-view.component.html',
    standalone: false
})
export class DepartmentDetailViewComponent extends DetailView implements OnInit {
   @Input() get department(): DepartmentDetailView | null {
      return this._department;
   }

   set department(value: DepartmentDetailView | null) {
      this._department = value;
      if (value) {
         this.formService.updateDepartment(value);
      }
   }

   @Input() set departmentPrivileges(privileges: DepartmentPrivileges) {
      this._departmentPrivileges = privileges;
      if (!privileges?.canEdit) {
         this.form.disable();
      }
   }

   get departmentPrivileges(): DepartmentPrivileges {
      return this._departmentPrivileges;
   }

   @Input() callState: CallState;

   @Output() formSaveCanceled = new EventEmitter<void>();

   @Output() formSubmitted = new EventEmitter<DepartmentDetailSaveEvent>();

   @Output() deleteDepartment = new EventEmitter<DepartmentDetailView>();

   @Output() renameDepartment = new EventEmitter<DepartmentDetailView>();

   @Output() deactivateDepartment = new EventEmitter<DepartmentDetailView>();

   @Output() mergeDepartment = new EventEmitter<DepartmentDetailView>();

   @Output() untagDepartment = new EventEmitter<DepartmentDetailView>();

   highlightedRow: number | null = null;

   form: UntypedFormGroup;

   shouldShowDetailView = true;

   columns = ['number', 'name'];

   private _department: DepartmentDetailView | null;

   private _departmentPrivileges: DepartmentPrivileges;

   private showDetailViewSubscription: Subscription;

   truncatedAccountName: string;

   constructor(
      public formService: DepartmentDetailFormService,
      private twoColumnLayoutService: TwoColumnLayoutService,
      private cdRef: ChangeDetectorRef
   ) {
      super();
      this.form = this.formService.form;
   }

   ngOnInit(): void {
      this.showDetailViewSubscription = this.twoColumnLayoutService.shouldShowDetailView$.subscribe((shouldShow) => {
         this.shouldShowDetailView = shouldShow;
         this.cdRef.markForCheck();
      });

      this.twoColumnLayoutService.deleteDepartment$.subscribe(() => {
         this.onDeleteClick();
      });
   }

   onSubmit(): void {
      const submitEvent = this.getFormSubmittedEvent();
      if (submitEvent) {
         this.formSubmitted.emit(submitEvent);
      }
   }

   onCancel(): void {
      this.formSaveCanceled.emit();
   }

   canDelete(): boolean {
      return this._departmentPrivileges.canDelete;
   }

   onDeleteClick(): void {
      if (this.department) {
         this.deleteDepartment.emit(this.department);
      }
   }

   onDeactivateClick(): void {
      if (this.department) {
         this.deactivateDepartment.emit(this.department);
      }
   }

   onRenameClick(): void {
      if (this.department) {
         this.renameDepartment.emit(this.department);
      }
   }

   onMergeClick(): void {
      if (this.department) {
         this.mergeDepartment.emit(this.department);
      }
   }

   onUntagClick(): void {
      if (this.department) {
         this.untagDepartment.emit(this.department);
      }
   }

   getRouterLink(id: number) {
      return `/${extensionsRoute}/${id}`;
   }

   private getFormSubmittedEvent(): DepartmentDetailSaveEvent | null {
      if (!this.formService.form.dirty || !this.department) {
         return null;
      }
      const department = this.formService.getValue(this.department.departmentId);
      let departmentFields: (keyof DepartmentInput)[] = [];
      departmentFields = this.formService.addFieldForDirtyControl(departmentFields, 'name');

      return { department, departmentFields };
   }
}
